// Pure, framework-free helpers for the cross-company "promote integration" flow: duplicating a
// validated test/shadow-company setup onto the client's real company (the Workato-cutover path).
// The CLAUS half (org, integration, webhooks) travels through the claus-proxy broker; the ERIN half
// (jobSyncConfig) is rebuilt here from the source company's config. Kept out of React and free of
// app bootstrap so it runs under plain jest, mirroring _shared/services/job-sync/jobSyncConfig.js.

import { cloneConfig } from '../job-sync/jobSyncConfig';

// The four promote steps, in execution order. `duplicate` is the one NON-idempotent step: a blind
// re-POST creates a second copy, so the orchestrator must never re-run it once an id is known.
export const STEP_ORDER = ['ensureOrg', 'duplicate', 'webhooks', 'config'];

export const STEP_LABELS = {
	ensureOrg: 'CLAUS organization',
	duplicate: 'Duplicate integration',
	webhooks: 'Copy webhooks',
	config: 'Copy job-sync config',
};

// CLAUS rejects duplicates whose target org lives under another customer (one API key == one
// customer, no admin bypass). Detecting the message lets the UI attach a "known limitation" hint.
export const CLAUS_CROSS_CUSTOMER_RE = /target organization belongs to a different customer/i;

// Broker errors are message-only (the 200-wrapped envelope loses the HTTP status), so a missing org
// is detected from the text — the same word-bounded sniff endpoints.ensureOrg uses.
export const NOT_FOUND_RE = /\b404\b|not found/i;

export function initialSteps() {
	const steps = {};
	for (const key of STEP_ORDER) steps[key] = { status: 'pending', detail: '', error: null };
	return steps;
}

// Local "parse if it's a JSON string, else pass through" — atsIntegration is an AWSJSON string on
// the wire but may arrive pre-parsed. Duplicated from jobSyncConfig.js (it isn't exported there).
function parseMaybe(value) {
	if (typeof value !== 'string') return value;
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
}

// CLAUS list endpoints answer either a bare array or a keyed object ({ items } / { webhooks }) —
// normalize once so callers don't repeat the dance.
export function listItems(res, key) {
	if (Array.isArray(res)) return res;
	if (res && Array.isArray(res[key])) return res[key];
	if (res && Array.isArray(res.items)) return res.items;
	return [];
}

// The promoted config: identical to the source except re-pointed at the target company. `mode` is
// ALWAYS forced back to dry-run — a promoted config must never be able to write on its first run,
// whatever the source said. Bonus / department references are names resolved server-side, so they
// travel verbatim (they must exist on the target — the prod company is normally where the shadow's
// names were mirrored FROM in the first place).
export function buildPromotedConfig(sourceConfig, { targetCompanyId, targetAtsName }) {
	const config = cloneConfig(sourceConfig);
	config.companyId = targetCompanyId;
	config.organizationId = targetCompanyId; // invariant: CLAUS org id == ERIN company id
	config.erinConnect = { ...(config.erinConnect || {}), type: targetAtsName };
	config.mode = 'dry-run';
	return config;
}

// Where would the promoted config land, and would it even take effect? process-jobs loads the FIRST
// atsIntegration entry that carries a jobSyncConfig — ATSName is NOT part of that lookup — while
// writeConfigIntoAtsIntegration targets the first entry whose ATSName matches. A config already
// sitting on an earlier entry therefore shadows the promoted one at runtime. `winnerAfterWrite`
// names the entry the engine would actually read after our write; `shadowed` flags when that isn't
// the entry we wrote.
export function assessTargetEntries(targetAtsIntegration, targetAtsName) {
	const raw = parseMaybe(targetAtsIntegration);
	const list = Array.isArray(raw) ? raw.filter((e) => e && typeof e === 'object') : [];
	const entries = list
		.filter((e) => e.ATSName)
		.map((e) => ({ atsName: e.ATSName, hasConfig: e.jobSyncConfig != null }));
	const chosen = entries.find((e) => e.atsName === targetAtsName) || null;
	const otherConfigEntries = list
		.filter((e) => e.jobSyncConfig != null && e.ATSName !== targetAtsName)
		.map((e) => e.ATSName || '(unnamed entry)');
	let winnerAfterWrite = null;
	for (const e of list) {
		if (e.jobSyncConfig != null || e.ATSName === targetAtsName) {
			winnerAfterWrite = e.ATSName || '(unnamed entry)';
			break;
		}
	}
	return {
		entries,
		chosen,
		overwrite: !!(chosen && chosen.hasConfig),
		otherConfigEntries,
		winnerAfterWrite,
		shadowed: !!(chosen && winnerAfterWrite !== targetAtsName),
	};
}

// Webhooks to create on the duplicate: the source's, minus any endpoint already present on the
// target (so retries stay idempotent). The process-jobs URL embeds a stack-wide token and the org
// id rides in the delivery payload, so endpoints copy verbatim across orgs. Source-side endpoint
// dupes collapse to one.
export function diffWebhooks(sourceHooks, targetHooks) {
	const seen = new Set();
	for (const w of Array.isArray(targetHooks) ? targetHooks : []) {
		if (w && w.endpoint) seen.add(w.endpoint);
	}
	const toCreate = [];
	const skipped = [];
	for (const w of Array.isArray(sourceHooks) ? sourceHooks : []) {
		if (!w || !w.endpoint) continue;
		if (seen.has(w.endpoint)) {
			skipped.push(w);
		} else {
			seen.add(w.endpoint);
			toCreate.push({ name: w.name || 'webhook', endpoint: w.endpoint, enabled: w.enabled !== false });
		}
	}
	return { toCreate, skipped };
}

// Case-insensitive name lookup on the target's integrations (pre-flight for the non-idempotent
// duplicate: a same-named integration means a previous promote already ran — adopt it, don't copy).
export function findNameCollision(integrations, name) {
	const wanted = String(name || '')
		.trim()
		.toLowerCase();
	if (!wanted) return null;
	const items = listItems(integrations, 'items');
	return items.find((i) => i && String(i.name || '').trim().toLowerCase() === wanted) || null;
}

// The offline fallback (and the reviewed-config-file path) for the ERIN half of a promote. Matches
// process-jobs/scripts/seed-config.ts usage; --mode dry-run mirrors buildPromotedConfig's forcing.
export function buildSeedCommand({ targetCompanyId, atsName, fileName = 'config/<client>-claus.json' }) {
	return [
		'AWS_PROFILE=enterprise npx tsx process-jobs/scripts/seed-config.ts',
		`  --config ${fileName}`,
		`  --company-id ${targetCompanyId}`,
		`  --ats-name '${atsName}'`,
		'  --mode dry-run',
	].join(' \\\n');
}
